/**
 * Очередь накладных — фильтры по разделам со счётчиками, поиск, список.
 *
 * Список перечитывается при возврате на вкладку и раз в 30 секунд
 * (§3 ТЗ портала): отметка, сделанная в боте, должна исчезнуть отсюда без
 * кнопки «обновить».
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { portalApi } from '../../api';
import type { CorrectionList, Queue as QueueData, QueueItem, SectionCode } from '../../api';
import { navigate } from '../../router';
import { Icon } from '../../icons';
import { Dot, when } from '../../ui';
import { Archive } from './Archive';
import { Duplicates } from './Duplicates';

export const SECTION_CODES: SectionCode[] = ['check', 'ready', 'wait', 'posted', 'dup'];

export const SECTION_TITLES: Record<SectionCode, string> = {
  check: 'Проверить',
  ready: 'Можно проводить',
  wait: 'Ждут черновика',
  posted: 'Проведено с расхождением',
  dup: 'Дубли',
};

const EMPTY_TEXT: Record<SectionCode, string> = {
  check: 'Проверять нечего — все сопоставленные накладные сошлись.',
  ready: 'Готовых к проведению накладных нет.',
  wait: 'Все сданные фото нашли свой черновик.',
  posted: 'Проведённых с расхождением нет.',
  dup: 'Дублей в StoreHouse нет.',
};

export function queueKey(section: SectionCode) {
  return ['receipts', 'queue', section] as const;
}

export function Queue({
  section,
  selectedId,
  onSection,
  onOpen,
  onCollapse,
  money = false,
}: {
  section: SectionCode;
  selectedId: number | null;
  onSection: (section: SectionCode) => void;
  onOpen: (id: number) => void;
  /** Спрятать очередь, чтобы фото и черновику досталось больше места. */
  onCollapse?: () => void;
  /** Право «Оплаты»: поиск в архиве по сумме. */
  money?: boolean;
}) {
  const [search, setSearch] = useState('');
  // Архив — не раздел сервера, а режим очереди: поиск по фото всех приёмок.
  const [archive, setArchive] = useState(false);
  const query = useQuery<QueueData, ApiError>({
    queryKey: queueKey(section),
    queryFn: () => portalApi.queue(section),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });

  const fixes = useQuery<CorrectionList, ApiError>({
    queryKey: ['receipts', 'corrections', 'open'],
    queryFn: () => portalApi.corrections('open'),
    refetchOnWindowFocus: true,
    retry: false,
  });

  const counts = query.data?.counts;
  const needle = search.trim().toLowerCase();
  const items = (query.data?.items ?? []).filter(
    (item) =>
      !needle ||
      [item.supplier, item.title, item.reason, item.draft_label]
        .filter(Boolean)
        .some((text) => (text as string).toLowerCase().includes(needle)),
  );

  return (
    <section className="queue" aria-label="Очередь накладных">
      <div className="queue__head">
        <h1 className="queue__title">Накладные</h1>
        {counts && counts.questions > 0 && (
          <span className="queue__questions">вопросов без ответа: {counts.questions}</span>
        )}
        {onCollapse && (
          <button
            type="button"
            className="icon-button queue__collapse"
            onClick={onCollapse}
            aria-label="Спрятать список накладных"
            title="Спрятать список накладных"
          >
            <Icon name="panel" size={18} />
          </button>
        )}
      </div>
      {!archive && (
        <label className="search">
          <Icon name="search" size={16} />
          <input
            className="search__input"
            type="search"
            placeholder="Поставщик или номер"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Поиск накладной"
          />
        </label>
      )}
      <div className="chips" role="tablist" aria-label="Разделы очереди">
        {SECTION_CODES.map((code) => (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={!archive && code === section}
            className={`chip${!archive && code === section ? ' chip--active' : ''}`}
            onClick={() => {
              setArchive(false);
              onSection(code);
            }}
          >
            {SECTION_TITLES[code]}
            {counts && <span className="chip__count">{counts[code] ?? 0}</span>}
          </button>
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={archive}
          className={`chip${archive ? ' chip--active' : ''}`}
          onClick={() => setArchive(true)}
        >
          <Icon name="search" size={14} /> Архив
        </button>
        <button type="button" className="chip" onClick={() => navigate('/receipts/fixes')}>
          Исправления в StoreHouse
          {fixes.data && fixes.data.counts.open > 0 && (
            <span className="chip__count">{fixes.data.counts.open}</span>
          )}
        </button>
      </div>

      {archive ? (
        <Archive selectedId={selectedId} onOpen={onOpen} money={money} />
      ) : (
        <div className="queue__list">
          {query.isPending ? (
            <Loading />
          ) : query.error ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : section === 'dup' ? (
            <Duplicates groups={query.data.duplicates} search={needle} />
          ) : items.length === 0 ? (
            <Empty
              title={needle ? 'Ничего не нашлось' : 'Пусто'}
              text={needle ? 'Попробуйте другое название или номер.' : EMPTY_TEXT[section]}
            />
          ) : (
            <ul className="queue-items">
              {items.map((item) => (
                <QueueRow
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  onOpen={() => onOpen(item.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function QueueRow({
  item,
  selected,
  onOpen,
}: {
  item: QueueItem;
  selected: boolean;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        className={`queue-item${selected ? ' queue-item--selected' : ''}`}
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
      >
        <span className="queue-item__top">
          <span className="queue-item__supplier">{item.supplier ?? 'Поставщик не узнан'}</span>
          <span className="queue-item__time">{when(item.received_at)}</span>
        </span>
        <span className="queue-item__title">{item.title}</span>
        {item.reason && (
          <span className="queue-item__reason">
            <Dot colour={item.colour} />
            {item.reason}
          </span>
        )}
      </button>
    </li>
  );
}
